import { useState, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import type { SortDescriptor } from 'react-aria-components'
import { TableBody as AriaTableBody } from 'react-aria-components'
import axios from 'axios'
import { Search } from 'lucide-react'
import { PlusIcon, Trash01Icon } from '@untitledui-icons/react/line'

import { Table, TableCard } from '@/components/application/table/table'
import { PaginationTable } from '@/components/application/pagination/pagination'
import { Button } from '@/components/base/buttons'
import { Badge } from '@/components/ui/Badge'
import { Input } from '@/components/ui/Input'
import { LoadingSpinner, EmptyState } from '@/components/common'
import { FormInput } from '@/components/forms/FormInput'
import { AlertDialog } from '@/components/ui/AlertDialog'
import {
  usePhoneNumbersAdmin,
  useCreatePhoneNumber,
  useDeletePhoneNumber,
  type PhoneNumber,
  type WhatsAppRegistrationStatus,
} from '@/hooks/api'
import { useListPageSearch } from '@/hooks/useListPageSearch'
import { toast } from '@/lib/toast'
import { PhoneNumberFilters, type PhoneNumberFilterValues } from './PhoneNumberFilters'

const E164_REGEX = /^\+[1-9]\d{1,14}$/

const phoneNumberSchema = z.object({
  number: z
    .string()
    .min(1, 'common.errors.required')
    .regex(E164_REGEX, 'resources.phone_numbers.invalid_e164'),
})

type PhoneNumberFormData = z.infer<typeof phoneNumberSchema>

export function PhoneNumbersPage() {
  const { t } = useTranslation()
  const [deleteTarget, setDeleteTarget] = useState<PhoneNumber | null>(null)

  const {
    searchQuery,
    handleSearch,
    filters,
    updateFilters,
    hasActiveFilters,
    page,
    perPage,
    handlePageChange,
    handlePageSizeChange,
  } = useListPageSearch<PhoneNumberFilterValues>({
    useJsonFilter: true,
    debounceMs: 300,
    persistKey: 'phone_numbers',
  })

  const [sortDescriptor, setSortDescriptor] = useState<SortDescriptor>({
    column: 'created_at',
    direction: 'descending',
  })

  const sort = useMemo(
    () => ({
      field: sortDescriptor.column as string,
      order: (sortDescriptor.direction === 'ascending' ? 'ASC' : 'DESC') as 'ASC' | 'DESC',
    }),
    [sortDescriptor]
  )

  const filter = useMemo(
    () => ({
      ...(filters.q ? { number: `%${filters.q}%` } : {}),
      ...(filters.whatsapp_status ? { whatsapp_status: filters.whatsapp_status } : {}),
      ...(filters.twilio_only ? { twilio_only: true } : {}),
    }),
    [filters]
  )

  const { data, isLoading, isFetching } = usePhoneNumbersAdmin({
    page,
    perPage,
    sort,
    filter,
  })

  const phoneNumbers = data?.data || []
  const totalCount = data?.total || 0
  const totalPages = Math.ceil(totalCount / perPage)

  const createPhoneNumber = useCreatePhoneNumber()
  const deletePhoneNumber = useDeletePhoneNumber()

  const { control, handleSubmit, reset, formState } = useForm<PhoneNumberFormData>({
    resolver: zodResolver(phoneNumberSchema),
    defaultValues: { number: '' },
  })

  const handleSortChange = useCallback((descriptor: SortDescriptor) => {
    setSortDescriptor(descriptor)
  }, [])

  const onSubmit = useCallback(
    async (formData: PhoneNumberFormData) => {
      try {
        await createPhoneNumber.mutateAsync({ number: formData.number })
        toast.success(t('resources.phone_numbers.created'))
        reset()
      } catch (error) {
        if (axios.isAxiosError(error) && error.response?.status === 409) {
          toast.error(t('resources.phone_numbers.duplicate_error'))
        } else {
          toast.error(t('resources.phone_numbers.create_error'))
        }
      }
    },
    [createPhoneNumber, reset, t]
  )

  const handleDelete = useCallback(async () => {
    if (!deleteTarget) return
    try {
      await deletePhoneNumber.mutateAsync(deleteTarget.id)
      toast.success(t('resources.phone_numbers.deleted'))
      setDeleteTarget(null)
    } catch {
      toast.error(t('resources.phone_numbers.delete_error'))
    }
  }, [deleteTarget, deletePhoneNumber, t])

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return '-'
    return new Date(dateStr).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  const whatsappStatusVariant: Record<WhatsAppRegistrationStatus, 'gray' | 'warning' | 'success' | 'error'> = {
    pending: 'warning',
    online: 'success',
    offline: 'gray',
    failed: 'error',
  }

  const hasFiltersOrSearch = hasActiveFilters || !!searchQuery
  const showInitialLoading = isLoading && !data && !hasFiltersOrSearch

  if (showInitialLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  return (
    <div className="flex h-full flex-col gap-6 p-6">
      <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-xs dark:border-gray-800 dark:bg-gray-900">
        <h2 className="mb-4 text-lg font-semibold text-gray-900 dark:text-gray-100">
          {t('resources.phone_numbers.add_title')}
        </h2>
        <p className="mb-4 text-sm text-gray-500 dark:text-gray-400">
          {t('resources.phone_numbers.add_description')}
        </p>
        <form onSubmit={handleSubmit(onSubmit)} className="flex items-start gap-3">
          <div className="w-full max-w-sm">
            <FormInput
              control={control}
              name="number"
              label={t('resources.phone_numbers.fields.number')}
              placeholder="+31612345678"
            />
          </div>
          <div className="mt-6">
            <Button
              type="submit"
              color="primary"
              size="md"
              iconLeading={PlusIcon}
              isLoading={formState.isSubmitting}
            >
              {t('resources.phone_numbers.add_button')}
            </Button>
          </div>
        </form>
      </div>

      <TableCard.Root size="sm" className="flex flex-1 flex-col">
        <TableCard.Header
          title={t('resources.phone_numbers.overview')}
          badge={<span className="text-md font-normal text-gray-500 dark:text-gray-400">({totalCount})</span>}
          contentTrailing={
            <div className="flex flex-wrap items-center gap-2 sm:gap-3">
              <Input
                type="text"
                placeholder={t('resources.phone_numbers.search_placeholder')}
                value={searchQuery}
                onChange={handleSearch}
                leftIcon={<Search className="h-5 w-5" />}
                className="w-40 sm:w-56"
              />
              <PhoneNumberFilters filters={filters} onFiltersChange={updateFilters} />
            </div>
          }
        />

        {isFetching && (
          <div className="h-1 w-full overflow-hidden bg-gray-100">
            <div className="h-full w-1/3 animate-pulse bg-brand-500" />
          </div>
        )}

        {phoneNumbers.length === 0 ? (
          <div className="flex flex-1 items-center justify-center p-6">
            <EmptyState
              title={t('resources.phone_numbers.empty')}
              description={t('resources.phone_numbers.empty_description')}
            />
          </div>
        ) : (
          <div className="flex-1 overflow-auto">
            <Table
              aria-label={t('resources.phone_numbers.overview')}
              sortDescriptor={sortDescriptor}
              onSortChange={handleSortChange}
            >
              <Table.Header>
                <Table.Head id="id" label={t('resources.phone_numbers.fields.id')} allowsSorting isRowHeader />
                <Table.Head id="number" label={t('resources.phone_numbers.fields.number')} allowsSorting />
                <Table.Head id="assigned" label={t('resources.phone_numbers.fields.assigned_to')} />
                <Table.Head id="whatsapp_status" label={t('resources.phone_numbers.fields.whatsapp_status')} />
                <Table.Head id="meta" label={t('resources.phone_numbers.fields.meta')} />
                <Table.Head id="twilio" label={t('resources.phone_numbers.fields.twilio')} />
                <Table.Head id="created_at" label={t('resources.phone_numbers.fields.created_at')} allowsSorting />
                <Table.Head id="actions" label="" />
              </Table.Header>

              <AriaTableBody>
                {phoneNumbers.map((phone) => {
                  const isAssigned = !!phone.assigned_assistant_id
                  return (
                    <Table.Row key={phone.id} id={phone.id}>
                      <Table.Cell>
                        <span className="text-sm font-medium text-gray-900 dark:text-gray-100">{phone.id}</span>
                      </Table.Cell>
                      <Table.Cell>
                        <span className="font-mono text-sm text-gray-900 dark:text-gray-100">{phone.number}</span>
                      </Table.Cell>
                      <Table.Cell>
                        {isAssigned ? (
                          <a
                            href={`/assistants/${phone.assigned_assistant_id}`}
                            className="text-sm text-brand-600 hover:underline dark:text-brand-400"
                          >
                            {phone.assigned_assistant_name}
                          </a>
                        ) : (
                          <span className="text-sm text-gray-400 dark:text-gray-500">
                            {t('resources.phone_numbers.unassigned')}
                          </span>
                        )}
                      </Table.Cell>
                      <Table.Cell>
                        {phone.whatsapp_status ? (
                          <Badge variant={whatsappStatusVariant[phone.whatsapp_status]} size="sm">
                            {phone.whatsapp_status}
                          </Badge>
                        ) : (
                          <span className="text-sm text-gray-400 dark:text-gray-500">—</span>
                        )}
                      </Table.Cell>
                      <Table.Cell>
                        <div className="flex flex-col gap-0.5 text-xs">
                          {phone.waba_id || phone.meta_phone_number_id ? (
                            <>
                              {phone.waba_id && (
                                <span className="font-mono text-gray-700 dark:text-gray-300" title={`WABA ${phone.waba_id}`}>
                                  WABA {phone.waba_id}
                                </span>
                              )}
                              {phone.meta_phone_number_id && (
                                <span className="font-mono text-gray-500 dark:text-gray-400" title={`Phone ID ${phone.meta_phone_number_id}`}>
                                  Phone ID {phone.meta_phone_number_id}
                                </span>
                              )}
                            </>
                          ) : (
                            <span className="text-gray-400 dark:text-gray-500">—</span>
                          )}
                        </div>
                      </Table.Cell>
                      <Table.Cell>
                        <div className="flex flex-col gap-0.5 text-xs">
                          {phone.twilio_subaccount_sid || phone.messaging_service_sid || phone.twilio_sender_sid ? (
                            <>
                              {phone.twilio_subaccount_sid && (
                                <span className="font-mono text-gray-700 dark:text-gray-300" title={phone.twilio_subaccount_sid}>
                                  AC {phone.twilio_subaccount_sid.slice(-6)}
                                </span>
                              )}
                              {phone.messaging_service_sid && (
                                <span className="font-mono text-gray-500 dark:text-gray-400" title={phone.messaging_service_sid}>
                                  MG {phone.messaging_service_sid.slice(-6)}
                                </span>
                              )}
                              {phone.twilio_sender_sid && (
                                <span className="font-mono text-gray-500 dark:text-gray-400" title={phone.twilio_sender_sid}>
                                  XE {phone.twilio_sender_sid.slice(-6)}
                                </span>
                              )}
                            </>
                          ) : (
                            <span className="text-gray-400 dark:text-gray-500">—</span>
                          )}
                        </div>
                      </Table.Cell>
                      <Table.Cell>
                        <span className="text-sm text-gray-500 dark:text-gray-400">{formatDate(phone.created_at)}</span>
                      </Table.Cell>
                      <Table.Cell>
                        <Button
                          color="tertiary-destructive"
                          size="sm"
                          iconLeading={Trash01Icon}
                          isDisabled={isAssigned}
                          onPress={() => setDeleteTarget(phone)}
                          aria-label={t('resources.phone_numbers.delete_button')}
                        />
                      </Table.Cell>
                    </Table.Row>
                  )
                })}
              </AriaTableBody>
            </Table>
          </div>
        )}

        {totalPages > 1 && (
          <PaginationTable
            page={page}
            total={totalPages}
            perPage={perPage}
            onPageChange={handlePageChange}
            onPerPageChange={handlePageSizeChange}
          />
        )}
      </TableCard.Root>

      <AlertDialog
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        variant="error"
        title={t('resources.phone_numbers.delete_title')}
        description={t('resources.phone_numbers.delete_description', {
          number: deleteTarget?.number,
        })}
        confirmLabel={t('resources.phone_numbers.delete_confirm')}
        cancelLabel={t('components.confirm_dialog.cancel')}
        isLoading={deletePhoneNumber.isPending}
      />
    </div>
  )
}

export default PhoneNumbersPage
